import { useCallback, useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { ELIGIBILITY_REMOVE_SUCCESS_TOAST } from "@/billing/constants/eligibility";
import type { EligibilityTableRow } from "@/billing/services/eligibility-table";
import {
  excludeAppointmentFromEligibilityList,
  fetchEligibilityTableRows,
} from "@/billing/services/eligibility-table";
import type { EligibilityAppointmentDateRange } from "@/billing/types/eligibility-appointment-date-range";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

export interface UseEligibilityTableDataResult {
  rows: EligibilityTableRow[];
  isLoading: boolean;
  error: Error | undefined;
  refetch: () => Promise<void>;
  excludeFromList: (
    appointmentId: string,
    clientName: string,
    appointmentLabel: string,
  ) => Promise<void>;
}

/**
 * Loads eligibility table rows for a practice appointment window.
 *
 * @param practiceId Practice organization id.
 * @param refreshKey Changing value that forces a refetch.
 * @param dateRange Appointment window to load. While undefined (for example, until the practice
 * timezone resolves), nothing is fetched and the hook reports loading.
 */
export function useEligibilityTableData(
  practiceId: string | undefined,
  refreshKey = 0,
  dateRange: EligibilityAppointmentDateRange | undefined,
): UseEligibilityTableDataResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<EligibilityTableRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const latestFetchRequestIdRef = useRef(0);
  const activeOperationCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const dateRangeStartTimestamp = dateRange?.startDate.getTime();
  const dateRangeEndTimestamp = dateRange?.endDate.getTime();
  const isWaitingForDateRange =
    dateRangeStartTimestamp === undefined || dateRangeEndTimestamp === undefined;

  /** Updates loading state from the count of in-flight async operations. */
  const updateLoadingState = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }
    setIsLoading(activeOperationCountRef.current > 0);
  }, []);

  /** Records that one async hook operation started. */
  const startOperation = useCallback(() => {
    activeOperationCountRef.current += 1;
    updateLoadingState();
  }, [updateLoadingState]);

  /** Records that one async hook operation finished. */
  const endOperation = useCallback(() => {
    activeOperationCountRef.current = Math.max(0, activeOperationCountRef.current - 1);
    updateLoadingState();
  }, [updateLoadingState]);

  const fetch = useCallback(async () => {
    if (!practiceId) {
      latestFetchRequestIdRef.current += 1;
      setRows([]);
      setError(undefined);
      updateLoadingState();
      return;
    }
    if (dateRangeStartTimestamp === undefined || dateRangeEndTimestamp === undefined) {
      latestFetchRequestIdRef.current += 1;
      return;
    }
    const requestId = ++latestFetchRequestIdRef.current;
    startOperation();
    setError(undefined);
    try {
      const data = await fetchEligibilityTableRows(medplum, practiceId, {
        startDate: new Date(dateRangeStartTimestamp),
        endDate: new Date(dateRangeEndTimestamp),
      });
      if (requestId !== latestFetchRequestIdRef.current || !isMountedRef.current) {
        return;
      }
      setRows(data.rows);
    } catch (err) {
      if (requestId !== latestFetchRequestIdRef.current || !isMountedRef.current) {
        return;
      }
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      Sentry.captureException(e);
      showSystemErrorNotification();
      setRows([]);
    } finally {
      endOperation();
    }
  }, [
    dateRangeEndTimestamp,
    dateRangeStartTimestamp,
    endOperation,
    medplum,
    practiceId,
    startOperation,
    updateLoadingState,
  ]);

  useEffect(() => {
    void fetch();
  }, [fetch, refreshKey]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const excludeFromList = useCallback(
    async (appointmentId: string, clientName: string, appointmentLabel: string) => {
      startOperation();
      try {
        await excludeAppointmentFromEligibilityList(medplum, appointmentId);
        await fetch();
        if (!isMountedRef.current) {
          return;
        }
        toast({
          type: "success",
          autoClose: 5000,
          title: ELIGIBILITY_REMOVE_SUCCESS_TOAST.title,
          message: ELIGIBILITY_REMOVE_SUCCESS_TOAST.message(clientName, appointmentLabel),
        });
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        showSystemErrorNotification();
      } finally {
        endOperation();
      }
    },
    [endOperation, medplum, fetch, startOperation],
  );

  return {
    rows,
    isLoading: isLoading || (Boolean(practiceId) && isWaitingForDateRange),
    error,
    refetch: fetch,
    excludeFromList,
  };
}
